/**
 * CompactDeleteSections — the compact popover body for a DELETE statement.
 * Destructive target line + the statement's REAL WHERE pills.
 *
 * DML-14/16/21 — the banner (`DmlTargetCard variant="banner"`) resolves an
 * mssql-style alias target (`DELETE p FROM Production.Product p …`) against
 * the FROM tail using the SAME helper trio + call pattern `DeleteRenderer`
 * uses for its full-view target pill (#10), and a read-only From/Using +
 * Joins row + RETURNING row give visible context for clauses that only WHERE
 * is editable here — mirroring, not duplicating, `DeleteRenderer`'s span
 * slicing.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import type { DeleteOutline } from '../../render/delete/extractDeleteOutline';
import { DmlTargetCard } from '../../render/common/DmlTargetCard';
import { dmlWhereScope } from '../../render/common/dmlWhereScope';
import { extractFromSegmentJoins } from '../../render/select/extractFromJoins';
import { FilterPills } from '../../render/select/FilterPills';
import { KIND_LABELS } from '../../render/select/joinKindMaps';
import { splitTopLevelCommas } from '../../render/select/splitTopLevel';
import {
  buildAliasCandidatesFromText,
  formatDmlTargetText,
  resolveDmlTargetAlias,
} from '../../render/update/resolveDmlTargetAlias';

export interface CompactDeleteSectionsProps {
  readonly bufferText: string;
  readonly outline: DeleteOutline;
  readonly options: ParseOptions;
  readonly editContext: VqeEditContext | null;
  /**
   * Show the `DELETE <target>` banner row. The popover header already
   * names the target (with the same write accent) whenever it resolves one,
   * so the host turns this off then — the row only fills in when the header
   * could say no more than the verb. Defaults to on.
   */
  readonly showTargetBanner?: boolean;
}

// TODO(vqe-copy): consolidate into vqeCopy (lane #4 owns vqeCopy.ts this batch).
const RETURNING_CONTEXT_SUFFIX = 'edited in the full editor';

interface FromJoinSummary {
  readonly leadingText: string | null;
  readonly joinsText: string | null;
}

/** Flattens a comma-separated FROM/USING/multi-table span into "From"/"Using" + "Joins" text summaries. */
function summarizeFromJoins(
  bufferText: string,
  start: number,
  end: number,
  options: ParseOptions,
): FromJoinSummary {
  const leadingParts: string[] = [];
  const joinParts: string[] = [];
  for (const seg of splitTopLevelCommas(bufferText, start, end, options)) {
    const split = extractFromSegmentJoins(bufferText, seg.start, seg.end, options);
    const leadingText = split.leading.text || seg.text;
    if (leadingText) leadingParts.push(leadingText);
    for (const join of split.joins) {
      const label = KIND_LABELS[join.kind];
      joinParts.push(join.rhs.text ? `${label} ${join.rhs.text}` : label);
    }
  }
  return {
    leadingText: leadingParts.length > 0 ? leadingParts.join(', ') : null,
    joinsText: joinParts.length > 0 ? joinParts.join(', ') : null,
  };
}

export function CompactDeleteSections({
  bufferText,
  outline,
  options,
  editContext,
  showTargetBanner = true,
}: CompactDeleteSectionsProps) {
  const ctx = editContext ?? undefined;
  const whereSpan =
    outline.whereStart !== null && outline.whereEnd !== null
      ? { start: outline.whereStart, end: outline.whereEnd }
      : null;

  // Same no-WHERE detector as the Visual view: with no WHERE the WHERE row
  // turns red and says which rows change; the popover header carries the
  // red "No WHERE" badge.
  const whereScope = dmlWhereScope(outline);

  const hasPreFromTargets = outline.preFromTargetText !== null;

  // DML-14/15 parity — resolve an mssql-style alias-target DELETE
  // (`DELETE p FROM Production.Product p …`) the SAME way DeleteRenderer
  // resolves its full-view target pill (#10). Skips a genuine multi-target
  // comma list (`DELETE t1, t2 FROM …`) — that's real table names, not an alias.
  let bannerTargetText = hasPreFromTargets ? (outline.preFromTargetText as string) : (outline.targetText ?? '');

  if (
    hasPreFromTargets &&
    outline.preFromTargetText !== null &&
    !outline.preFromTargetText.includes(',') &&
    outline.targetStart !== null
  ) {
    const tailEnd = (outline.multiTableEnd ?? outline.targetEnd) as number;
    const candidates = buildAliasCandidatesFromText(bufferText, outline.targetStart, tailEnd, options);
    const resolved = resolveDmlTargetAlias(outline.preFromTargetText, candidates);
    bannerTargetText = formatDmlTargetText(outline.preFromTargetText, resolved);
  }

  // DML-16/21 — read-only From/Using + Joins context row, from whichever tail
  // the statement actually has (post-FROM target tail / MySQL form-A join, or
  // a PG/MySQL USING clause) — mirrors DeleteRenderer's Tables-card spans.
  let fromLabel = 'From';
  let fromRowText: string | null = null;
  let joinsRowText: string | null = null;

  if (hasPreFromTargets && outline.targetStart !== null) {
    const tailEnd = outline.multiTableEnd ?? outline.targetEnd;
    if (tailEnd !== null) {
      const summary = summarizeFromJoins(bufferText, outline.targetStart, tailEnd, options);
      fromRowText = summary.leadingText;
      joinsRowText = summary.joinsText;
    }
  } else if (outline.multiTableStart !== null && outline.multiTableEnd !== null) {
    const summary = summarizeFromJoins(bufferText, outline.multiTableStart, outline.multiTableEnd, options);
    fromRowText = summary.leadingText;
    joinsRowText = summary.joinsText;
  } else if (outline.usingStart !== null && outline.usingEnd !== null) {
    fromLabel = 'Using';
    const summary = summarizeFromJoins(bufferText, outline.usingStart, outline.usingEnd, options);
    fromRowText = summary.leadingText;
    joinsRowText = summary.joinsText;
  }

  return (
    <div className="flex flex-col gap-2" data-testid="vqe-compact-delete-sections">
      {showTargetBanner ? (
        <DmlTargetCard intent="delete" variant="banner" targetText={bannerTargetText} />
      ) : null}

      {fromRowText || joinsRowText ? (
        <div
          className="flex flex-col gap-0.5 rounded-md border border-border/60 bg-muted/20 px-2 py-1 text-[11px] text-muted-foreground"
          data-testid="vqe-compact-from-row"
        >
          {fromRowText ? (
            <span data-testid="vqe-compact-from-row-from">
              {fromLabel}: <span className="font-mono text-foreground/80">{fromRowText}</span>
            </span>
          ) : null}
          {joinsRowText ? (
            <span data-testid="vqe-compact-from-row-joins">
              Joins: <span className="font-mono text-foreground/80">{joinsRowText}</span>
            </span>
          ) : null}
        </div>
      ) : null}

      <FilterPills
        bufferText={bufferText}
        span={whereSpan}
        options={options}
        kind="where"
        editContext={ctx}
        density="compact"
        destructiveGuard={{ verb: 'DELETE', targetText: bannerTargetText, scope: whereScope }}
      />

      {outline.returningStart !== null && outline.returningEnd !== null ? (
        <div
          className="rounded-md border border-border/60 bg-muted/20 px-2 py-1 text-[11px] text-muted-foreground"
          data-testid="vqe-compact-returning-row"
        >
          <span className="font-mono text-foreground/80">
            RETURNING{' '}
            {splitTopLevelCommas(bufferText, outline.returningStart, outline.returningEnd, options)
              .map((item) => item.text)
              .join(', ')}
          </span>{' '}
          — {RETURNING_CONTEXT_SUFFIX}
        </div>
      ) : null}
    </div>
  );
}
